import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, FileText, Download } from 'lucide-react';
import { categoryApi, materialApi, uploadsUrl } from '../services/api';
import { useAuthStore } from '../store/authStore';

function Courses() {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('mba');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isStudent = user?.role === 'student';

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadMaterials();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const res = await categoryApi.list();
      setCategories(res.data || []);
    } catch (error) {
      // Categories are optional; continue without them
    }
  };


  const loadMaterials = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      const res = await materialApi.list(params);
      setMaterials(res.data || []);
    } catch (error) { /* ignore */ }
    finally { setIsLoading(false); }
  };

  const handleDownload = async (m) => {
    const fileUrl = uploadsUrl(m.file_path.replace('/uploads/', ''));
    try {
      await materialApi.trackDownload(m.id);
      window.open(fileUrl, '_blank');
    } catch (error) { window.open(fileUrl, '_blank'); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const fileIcon = { pdf: '📄', doc: '📝', docx: '📝', ppt: '📊', pptx: '📊', xls: '📈', xlsx: '📈', txt: '📃', md: '📃', jpg: '🖼️', jpeg: '🖼️', png: '🖼️' };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Study Materials</h1>
          <p className="text-gray-600 mt-1">
            {isStudent ? 'Browse and learn from available study materials' : 'View and manage study materials'}
          </p>
        </div>
        {!isStudent && (
          <button
            onClick={() => navigate('/lms/manage')}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Settings className="w-4 h-4 mr-2" />
            Manage LMS
          </button>
        )}
      </div>

      {isStudent && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <p className="text-sm text-blue-800">
            These materials are for reference to study topics and subjects. You can upload your own textbooks in <a href="/notebooks" className="font-semibold underline hover:text-blue-900">AI Notebooks</a> to learn with AI-powered tools like Quiz, Flashcards, Mind Maps and more.
          </p>
        </div>
      )}


      {/* Category tabs hidden for now - only showing MBA Entrance materials */}
      {/* {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === null
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.slug)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === cat.slug
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )} */}

      {/* Materials View */}
      {materials.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-500">No documents available yet</h3>
            <p className="text-gray-400 mt-1">Check back later for study materials</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {materials.map(m => (
              <div key={m.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-2xl flex-shrink-0">
                    {fileIcon[m.file_type] || '📎'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{m.title}</h3>
                    {m.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{m.description}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span className="uppercase font-medium">{m.file_type}</span>
                      <span>{formatSize(m.file_size)}</span>
                      {m.category_name && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{m.category_name}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">{new Date(m.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <button onClick={() => handleDownload(m)} className="flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs hover:bg-primary-700">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

    </div>
  );
}

export default Courses;
